import { BULLION_DEMO_MATCH_FLOW_PATH } from "../../Bullion/lib/bullionDemoMatchFixtures";
import { SQUARES_DEMO_MATCH_ENABLED, SQUARES_DEMO_MATCH_ID } from "../../Squares/lib/squaresDemoMatchFixtures";
import { isLeagueSixesDemoMatchId, isSixesDemoMatchId, SIXES_DEMO_MATCH_ID } from "../../Sixes/demo/sixesDemoMatch";
import { readSixesCardRevealed } from "../../Sixes/lib/sixesCardRevealStorage";
import { isPrototypeFixtureId } from "./leagueLiveFixtures";
import type { League, LeagueGameKey, LeagueStatus } from "./types";

/** Query param that keeps League mode active on real game routes. */
export const LEAGUE_ID_QUERY = "leagueId";

/**
 * Share-code query on the leagues hub (`/leagues?code=`).
 * Query param (not a path segment) so nginx’s single-segment SPA fallback serves hard loads.
 */
export const LEAGUE_JOIN_CODE_QUERY = "code";

/**
 * League-detail id query on the leagues hub (`/leagues?id=`).
 * Query param (not a path segment) so nginx’s single-segment SPA fallback serves hard loads
 * on refresh — same reason as the join `?code=` above.
 */
export const LEAGUE_DETAIL_ID_QUERY = "id";

/**
 * Hub list tab on `/leagues` / `/leaguesdemo` (`?tab=past` → Past).
 * Only applied when neither `id` nor `code` is present (those own the same path).
 */
export const LEAGUE_HUB_TAB_QUERY = "tab";

/**
 * Hub listing tab on `/leagues` / `/leaguesdemo` (`?listing=private` → Private).
 * Omitted when Public (the default). Only applied when neither `id` nor `code` is present.
 */
export const LEAGUE_HUB_LISTING_QUERY = "listing";

/** Client-side prototype island. Single-segment so nginx SPA fallback serves it. */
export const LEAGUES_DEMO_PATH = "/leaguesdemo";

/** Opens the Bullion demo board as this league’s shared private battle (skip arena picker). */
export const LEAGUE_BULLION_BATTLE_QUERY = "leagueBattle";

const GAME_PATHS: Record<LeagueGameKey, string> = {
    sixes: "/sixes",
    bullion: "/bullion",
    squares: "/squares",
    trader: "/trader",
};

export function readLeagueIdFromSearch(search: string): string | null {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const value = new URLSearchParams(raw).get(LEAGUE_ID_QUERY);
    return value?.trim() ? value.trim() : null;
}

export function withLeagueId(path: string, leagueId: string): string {
    const [pathname, query = ""] = path.split("?");
    const params = new URLSearchParams(query);
    params.set(LEAGUE_ID_QUERY, leagueId);
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
}

/** Detail URL: `/leagues?id=` or `/leaguesdemo?id=` (query param keeps nginx SPA fallback happy on refresh). */
export function leagueDetailPath(leagueId: string, basePath = "/leagues"): string {
    const trimmed = leagueId.trim();
    if (!trimmed) return basePath;
    const params = new URLSearchParams();
    params.set(LEAGUE_DETAIL_ID_QUERY, trimmed);
    return `${basePath}?${params}`;
}

export function readLeagueDetailIdFromSearch(search: string): string | null {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const value = new URLSearchParams(raw).get(LEAGUE_DETAIL_ID_QUERY);
    return value?.trim() ? value.trim() : null;
}

/** Join landing URL: `/leagues?code=` or `/leaguesdemo?code=`. */
export function leagueJoinPath(code: string, basePath = "/leagues", invitedBy = ""): string {
    const trimmed = code.trim();
    if (!trimmed) return basePath;
    const params = new URLSearchParams();
    params.set(LEAGUE_JOIN_CODE_QUERY, trimmed);
    const inviter = invitedBy.trim();
    if (inviter) params.set("invitedBy", inviter);
    return `${basePath}?${params}`;
}

export function readLeagueJoinCodeFromSearch(search: string): string | null {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const value = new URLSearchParams(raw).get(LEAGUE_JOIN_CODE_QUERY);
    return value?.trim() ? value.trim() : null;
}

/** Sharer public player id from `/leagues?code=&invitedBy=` (private-league auto-friend). */
export function readLeagueInvitedByFromSearch(search: string): string | null {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const params = new URLSearchParams(raw);
    const value = params.get("invitedBy") || params.get("invited_by");
    return value?.trim() ? value.trim() : null;
}

export type LeagueHubTab = "active" | "past";

/** Hub Public | Private listing. Public is the default (no query param). */
export type LeagueHubListing = "public" | "private";

export type LeagueHubPathOptions = {
    tab?: LeagueHubTab;
    listing?: LeagueHubListing;
    basePath?: string;
};

/** Hub URL: bare path = Public + Active; `?listing=private` / `?tab=past` as needed. */
export function leagueHubPath(
    tabOrOpts: LeagueHubTab | LeagueHubPathOptions = "active",
    basePathArg = "/leagues",
): string {
    const opts: LeagueHubPathOptions =
        typeof tabOrOpts === "string"
            ? { tab: tabOrOpts, basePath: basePathArg }
            : tabOrOpts;
    const tab = opts.tab ?? "active";
    const listing = opts.listing ?? "public";
    const basePath = opts.basePath ?? basePathArg;
    const params = new URLSearchParams();
    if (listing === "private") params.set(LEAGUE_HUB_LISTING_QUERY, "private");
    if (tab === "past") params.set(LEAGUE_HUB_TAB_QUERY, "past");
    const qs = params.toString();
    return qs ? `${basePath}?${qs}` : basePath;
}

/** `past` / `ended` / `finished` → Past tab; anything else → Active. */
export function readLeagueHubTabFromSearch(search: string): LeagueHubTab {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const value = new URLSearchParams(raw).get(LEAGUE_HUB_TAB_QUERY)?.trim().toLowerCase();
    if (value === "past" || value === "ended" || value === "finished") return "past";
    return "active";
}

/** `private` → Private listing; anything else → Public (default). */
export function readLeagueHubListingFromSearch(search: string): LeagueHubListing {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const value = new URLSearchParams(raw).get(LEAGUE_HUB_LISTING_QUERY)?.trim().toLowerCase();
    if (value === "private") return "private";
    return "public";
}

export function isLeaguesDemoPath(pathname: string): boolean {
    return pathname === LEAGUES_DEMO_PATH || pathname.startsWith(`${LEAGUES_DEMO_PATH}/`);
}

/** League detail page: `/leagues?id=` or `/leaguesdemo?id=` (not the hub or join landing). */
export function isLeagueDetailPath(pathname: string, search = ""): boolean {
    const base = isLeaguesDemoPath(pathname) ? LEAGUES_DEMO_PATH : "/leagues";
    const normalized = pathname.replace(/\/$/, "") || "/";
    if (normalized !== base) return false;
    return Boolean(readLeagueDetailIdFromSearch(search));
}

export function isLeagueGamePath(pathname: string): boolean {
    return (Object.values(GAME_PATHS) as string[]).some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function leagueGameKeyFromPath(pathname: string): LeagueGameKey | null {
    if (pathname === "/sixes" || pathname.startsWith("/sixes/")) return "sixes";
    if (pathname === "/bullion" || pathname.startsWith("/bullion/")) return "bullion";
    if (pathname === "/squares" || pathname.startsWith("/squares/")) return "squares";
    if (pathname === "/trader" || pathname.startsWith("/trader/")) return "trader";
    return null;
}

/** True when a league game route is in match/board play (not the game lobby). */
export function isLeagueGamePlayPath(pathname: string, search: string): boolean {
    return isLeagueGamePath(pathname) && !isGameLobbyWithoutMatch(pathname, search);
}

/** Bare lobby path for the game route the viewer is on, e.g. `/trader`. */
export function leagueGameLobbyPathFromPathname(pathname: string): string {
    const key = leagueGameKeyFromPath(pathname);
    return key ? GAME_PATHS[key] : "/trader";
}

/** True when a game route is showing its lobby (no match/play identifier). */
export function isGameLobbyWithoutMatch(pathname: string, search: string): boolean {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const game = leagueGameKeyFromPath(pathname);
    if (game === "sixes") return !params.get("match") && !params.get("matchId");
    if (game === "bullion") return !params.get("match") && !params.get("game") && !params.get("demo");
    if (game === "squares") return !params.get("match");
    if (game === "trader") return !params.get("matchId") && !params.get("raceId");
    return false;
}

/**
 * Where the in-game back control should go while League mode is active.
 * Match leagues (and unknown ids — typical deep-link into play) return to the league detail.
 * Timed leagues return to that game's lobby still tagged with `?leagueId=`.
 * Returns null when not in League mode.
 */
export function resolveLeagueGameBackHref(options: {
    leagueId: string | null | undefined;
    findLeague: (leagueId: string) => { scope: League["scope"] } | null;
    /** Bare game lobby path, e.g. `/sixes` or `/squares`. */
    gameLobbyPath: string;
    /** `/leagues` or `/leaguesdemo` — defaults to `/leagues`. */
    leagueBasePath?: string;
}): string | null {
    const id = String(options.leagueId || "").trim();
    if (!id) return null;
    const league = options.findLeague(id);
    if (!league || league.scope === "match") {
        return leagueDetailPath(id, options.leagueBasePath);
    }
    return withLeagueId(options.gameLobbyPath, id);
}

/** Real cricket event id, or null for proto/demo fixtures that cannot load play pages. */
function realMatchId(fixtureId: string): string | null {
    const id = fixtureId.trim();
    if (!id || isPrototypeFixtureId(id)) return null;
    return id;
}

function resolvedPlayMatchId(fixtureId: string, liveMatchId?: string | null): string | null {
    const live = String(liveMatchId || "").trim();
    if (live && !isPrototypeFixtureId(live)) return live;
    return realMatchId(fixtureId);
}

export type LeagueGamePlayUrlOptions = {
    /** When set for Sixes, live leagues open `mode=play`; upcoming stays reveal. */
    leagueStatus?: LeagueStatus;
    /** Sixes: force reveal vs already-revealed play open. */
    sixesMode?: "reveal" | "play";
    /** Live cricket event id when it differs from `league.fixture.id`. */
    matchId?: string;
    /** Sixes: open another member's league card (Activity peer summary). */
    sixesCardId?: number | null;
};

/** Match id used for league Sixes deep-links. Prototype fixtures keep their own India/WI demo id. */
export function leagueSixesPlayMatchId(fixtureId: string, liveMatchId?: string | null): string {
    const trimmed = fixtureId.trim();
    if (isPrototypeFixtureId(trimmed)) return trimmed;
    return resolvedPlayMatchId(fixtureId, liveMatchId) ?? SIXES_DEMO_MATCH_ID;
}

/**
 * Deep-link into the real game match-play UI for a league lobby.
 * Uses the live cricket event when available; demo match ids only
 * for leftover prototype leagues.
 */
export function leagueGamePlayUrl(
    league: League,
    game: LeagueGameKey,
    options?: LeagueGamePlayUrlOptions,
): string {
    const fixtureId = league.fixture?.id?.trim() ?? "";
    const matchId = resolvedPlayMatchId(fixtureId, options?.matchId);

    switch (game) {
        case "trader": {
            const params = new URLSearchParams();
            params.set(LEAGUE_ID_QUERY, league.id);
            params.set("sport", "cricket");
            params.set("matchId", matchId ?? (fixtureId || "league-match"));
            return `/trader?${params}`;
        }
        case "squares": {
            const prototypeLive =
                isPrototypeFixtureId(fixtureId) && (options?.leagueStatus ?? league.status) === "live";
            const match = isPrototypeFixtureId(fixtureId)
                ? fixtureId
                : SQUARES_DEMO_MATCH_ENABLED && (prototypeLive || !matchId)
                  ? SQUARES_DEMO_MATCH_ID
                  : matchId ?? fixtureId;
            const params = new URLSearchParams();
            params.set(LEAGUE_ID_QUERY, league.id);
            params.set("match", match);
            return `/squares?${params}`;
        }
        case "bullion": {
            if (matchId) {
                const params = new URLSearchParams();
                params.set(LEAGUE_ID_QUERY, league.id);
                params.set("match", matchId);
                return `/bullion?${params}`;
            }
            // Prototype leftover: shared battle on the demo pick board.
            const params = new URLSearchParams();
            const [pathname, query = ""] = BULLION_DEMO_MATCH_FLOW_PATH.split("?");
            new URLSearchParams(query).forEach((value, key) => params.set(key, value));
            params.set(LEAGUE_ID_QUERY, league.id);
            params.set(LEAGUE_BULLION_BATTLE_QUERY, "1");
            return `${pathname}?${params}`;
        }
        case "sixes": {
            const params = new URLSearchParams();
            params.set(LEAGUE_ID_QUERY, league.id);
            const sixesMatch = leagueSixesPlayMatchId(fixtureId, options?.matchId);
            params.set("match", sixesMatch);
            const peerCardId = Number(options?.sixesCardId);
            const hasPeerCard = Number.isFinite(peerCardId) && peerCardId > 0;
            const isDemo = isSixesDemoMatchId(sixesMatch);
            const presenterPrematch =
                isLeagueSixesDemoMatchId(sixesMatch) && !league.presenterMatchStarted;
            const mode = presenterPrematch
                ? "reveal"
                : options?.sixesMode ??
                  (hasPeerCard
                      ? "play"
                      : isDemo && typeof window !== "undefined" && readSixesCardRevealed(sixesMatch)
                        ? "play"
                        : "reveal");
            // Real league cards carry the mode too, so re-opening one skips the reveal animation.
            // Presenter India–WI stays reveal until Start the match, even if the card is already open.
            if (mode === "play") {
                params.set("mode", "play");
            }
            if (hasPeerCard) {
                params.set("cardId", String(Math.round(peerCardId)));
            }
            return `/sixes?${params}`;
        }
        default: {
            const _exhaustive: never = game;
            return _exhaustive;
        }
    }
}

/** Open a game's own lobby while staying in League mode (duration leagues). */
export function leagueGameLobbyUrl(league: League, game: LeagueGameKey): string {
    return withLeagueId(GAME_PATHS[game], league.id);
}
